import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Send, MessageSquare, Plus, Mail } from "lucide-react";
import { newRequestKey, localizeSendError } from "@/lib/emailSend";

// Slice 2D4 Batch 3 · Update-type enum keys. The DB stored value is
// the .key ("estimate_received", etc.) which never changes. The label
// is looked up via `t("update_type_labels.<key>")` so the display
// renders in the contractor's app language.
const UPDATE_TYPES = [
  { key: "estimate_received" },
  { key: "proposal_sent" },
  { key: "contract_ready" },
  { key: "deposit_invoice_sent" },
  { key: "project_scheduled" },
  { key: "project_delay" },
  { key: "change_order_needed" },
  { key: "final_invoice_sent" },
  { key: "thank_you_review" },
];

const CustomerUpdates = () => {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", update_type: "estimate_received", language: lang, custom_note: "" });
  const [sendingId, setSendingId] = useState(null);
  const sendingRef = useRef(null);

  useEffect(() => {
    api.get("/customer-updates").then((r) => setItems(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/customer-updates/templates").then((r) => setTemplates(r.data));
  }, []);

  useEffect(() => { setForm((f) => ({ ...f, language: lang })); }, [lang]);

  const preview = templates?.[form.update_type]?.[form.language] || "";

  const labelForType = (key) => {
    const dictKey = `update_type_labels.${key}`;
    const val = t(dictKey);
    return val === dictKey ? key : val;
  };

  const create = async () => {
    if (!form.project_id) { toast.error(t("customer_updates_page.toast_choose_project")); return; }
    try {
      const { data } = await api.post("/customer-updates", form);
      setItems([data, ...items]);
      setOpen(false);
      toast.success(t("customer_updates_page.toast_created"));
    } catch { toast.error(t("customer_updates_page.toast_create_failed")); }
  };

  return (
    <DashboardLayout
      title={t("pages.customer_updates.title")}
      subtitle={t("pages.customer_updates.subtitle")}
      actions={
        <button onClick={() => setOpen(true)} data-testid="update-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue">
          <Plus size={14}/> {t("updates_form.new_btn")}
        </button>
      }
    >
      {open && (
        <div className="jc-card p-6 mb-6 max-w-3xl">
          <div className="overline mb-3">{t("updates_form.generate_heading")}</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("updates_form.project")}</label>
              <select data-testid="update-project-select" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]">
                <option value="">{t("updates_form.choose_project")}</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("updates_form.type")}</label>
              <select data-testid="update-type-select" value={form.update_type} onChange={(e) => setForm({ ...form, update_type: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]">
                {UPDATE_TYPES.map((ut) => <option key={ut.key} value={ut.key}>{labelForType(ut.key)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("updates_form.language")}</label>
              <select data-testid="update-language-select" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]">
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("updates_form.custom_note")}</label>
            <textarea rows={2} value={form.custom_note} onChange={(e) => setForm({ ...form, custom_note: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]" />
          </div>
          <div className="mt-4 rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] p-4">
            <div className="overline mb-2">{t("updates_form.preview")}</div>
            <p className="text-sm whitespace-pre-wrap">{preview || t("updates_form.empty_dash")}</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-[var(--jc-text-soft)]">{t("updates_form.cancel")}</button>
            <button onClick={create} data-testid="update-create-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold jc-btn-primary"><Send size={14}/> {t("updates_form.generate")}</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("pages.customer_updates.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("pages.customer_updates.empty_body")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.id} className="jc-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <MessageSquare size={14} className="text-[var(--jc-blue)]" />
                    <span className="overline">{labelForType(u.update_type)}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)]">{u.language.toUpperCase()}</span>
                  </div>
                  <div className="font-display text-lg mt-1">{u.customer_name}</div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{u.message}</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-xs text-[var(--jc-text-soft)]">{formatDate(u.created_at, lang)}</div>
                  <button
                    onClick={async () => {
                      if (sendingRef.current) return;
                      const defaultEmail = u.customer_email || "";
                      const to = window.prompt(t("customer_updates_page.prompt_email_to"), defaultEmail);
                      if (!to) return;
                      const request_key = newRequestKey();
                      sendingRef.current = u.id;
                      setSendingId(u.id);
                      try {
                        const { data } = await api.post(`/customer-updates/${u.id}/send-email`, { to_email: to, request_key });
                        const label = data.mock ? t("customer_updates_page.toast_email_mock") : t("customer_updates_page.toast_email_sent");
                        toast.success(`${label} — ${to}`);
                      } catch (e) {
                        toast.error(localizeSendError(e, t, "customer_updates_page.toast_email_failed"));
                      } finally {
                        sendingRef.current = null;
                        setSendingId(null);
                      }
                    }}
                    disabled={sendingId === u.id}
                    data-testid={`update-email-btn-${u.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Mail size={12}/> {t("updates_form.email_update")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CustomerUpdates;
